import React, { useEffect, useState } from "react";
import Backdrop from "@mui/material/Backdrop";

import {
  cfopFrame,
  cofins_situacao_tributaria_frame,
  icms_origem_frame,
  icms_situacao_tributaria_frame,
  pis_situacao_tributaria_frame,
} from "../../../common/frames";
import { findValueInFrame } from "../../../utils/frames";
import { validateString } from "../../../common/validators";

// APP COMPONENTS
import ModalHeader from "../../modals/modalHeader";
import ItemFiscalDataForm from "../fiscal/ItemFiscalDataForm";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { LightGreyBox } from "../../styled";
import CircularProgress from "@mui/material/CircularProgress";

const ModalRoot = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  border: "none",
  borderRadius: 12,
  overflow: "auto",
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
    height: "95% !important",
    borderRadius: "0 0",
  },
  [theme.breakpoints.up("md")]: {
    width: "30% !important",
    height: "65% !important",
  },
  backgroundColor: "#fff",
  paddingRight: 24,
  paddingLeft: 24,
  paddingBottom: 24,
  paddingTop: 0,
  "&:focus-visible": {
    border: "none",
  },
}));

export default function ItemFiscalInfosModal({
  show,
  closeModal,
  product,
  updateProduct,
}) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Initialize all state with non-undefined values to maintain controlled components
  const [cfopCode, setCfopCode] = useState(cfopFrame[0] || null);
  const [icmsSituacaoTributariaState, setIcmsSituacaoTributariaState] =
    useState(icms_situacao_tributaria_frame[0] || null);
  const [icmsOrigemState, setIcmsOrigemState] = useState(
    icms_origem_frame[0] || null
  );
  const [icmsValue, setIcmsValue] = useState(0);

  const [pisSituacaoTributariaState, setPisSituacaoTributariaState] = useState(
    pis_situacao_tributaria_frame[0] || null
  );
  const [pisValue, setPisValue] = useState(0);

  const [cofinsSituacaoTributariaState, setCofinsSituacaoTributariaState] =
    useState(cofins_situacao_tributaria_frame[0] || null);
  const [cofinsValue, setCofinsValue] = useState(0);

  const [fiscalCode, setFiscalCode] = useState("");
  const [isFiscalCodeValid, setIsFiscalCodeValid] = useState(false);
  const [ifoodExternalCode, setIfoodExternalCode] = useState("");

  // Load data once when component mounts
  useEffect(() => {
    console.log("loadData", product);
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      // Ensure we always set non-undefined values
      const safeFind = (frame, value) => {
        const result = findValueInFrame(frame, value);
        return result || frame[0] || null;
      };

      setCfopCode(safeFind(cfopFrame, product?.cfop));
      setIcmsSituacaoTributariaState(
        safeFind(
          icms_situacao_tributaria_frame,
          product?.icms_situacao_tributaria
        )
      );
      setIcmsOrigemState(safeFind(icms_origem_frame, product?.icms_origem));
      setPisSituacaoTributariaState(
        safeFind(
          pis_situacao_tributaria_frame,
          product?.pis_situacao_tributaria
        )
      );
      setCofinsSituacaoTributariaState(
        safeFind(
          cofins_situacao_tributaria_frame,
          product?.cofins_situacao_tributaria
        )
      );

      // Set fiscalCode to empty string if undefined
      setFiscalCode(product?.fiscal_code || "");
      setIfoodExternalCode(product?.ifood_external_code || "");
    } catch (error) {
      console.error("Error loading fiscal data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    console.log("handleSubmit", product);
    try {
      if (validateString(fiscalCode)) {
        updateProduct({
          ...product,
          fiscal_code: fiscalCode,
          cfop: cfopCode?.value,
          icms_situacao_tributaria: icmsSituacaoTributariaState?.value,
          icms_origem: icmsOrigemState?.value,
          pis_situacao_tributaria: pisSituacaoTributariaState?.value,
          cofins_situacao_tributaria: cofinsSituacaoTributariaState?.value,
          ifood_external_code: ifoodExternalCode,
          is_product: !!product.product_id,
        });
        closeModal();
      } else {
        setSubmitted(true);
        setIsFiscalCodeValid(false);
      }
    } catch (error) {
      console.error("Error submitting fiscal data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={show}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{
        zIndex: 5000,
        "&:focus-visible": {
          border: "none",
        },
      }}
    >
      <Fade in={show}>
        <ModalRoot>
          {!loading ? (
            <>
              <ModalHeader
                label={product ? product.title : "Fiscal"}
                margin={0}
                onClose={closeModal}
              />
              <LightGreyBox>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12}>
                    <ItemFiscalDataForm
                      ifoodExternalCode={ifoodExternalCode}
                      setIfoodExternalCode={setIfoodExternalCode}
                      fiscalCode={fiscalCode}
                      setFiscalCode={setFiscalCode}
                      cofinsSituacaoTributariaState={
                        cofinsSituacaoTributariaState
                      }
                      setCofinsSituacaoTributariaState={
                        setCofinsSituacaoTributariaState
                      }
                      cofinsValue={cofinsValue}
                      setCofinsValue={setCofinsValue}
                      pisSituacaoTributariaState={pisSituacaoTributariaState}
                      setPisSituacaoTributariaState={
                        setPisSituacaoTributariaState
                      }
                      pisValue={pisValue}
                      setPisValue={setPisValue}
                      icmsOrigemState={icmsOrigemState}
                      setIcmsOrigemState={setIcmsOrigemState}
                      icmsSituacaoTributariaState={icmsSituacaoTributariaState}
                      setIcmsSituacaoTributariaState={
                        setIcmsSituacaoTributariaState
                      }
                      icmsValue={icmsValue}
                      setIcmsValue={setIcmsValue}
                      cfopCode={cfopCode}
                      setCfopCode={setCfopCode}
                      submitted={submitted}
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleSubmit}
                  >
                    SALVAR
                  </Button>
                </Stack>
              </LightGreyBox>
            </>
          ) : (
            <Box sx={{ display: "flex", height: "50%" }}>
              <CircularProgress
                sx={{ margin: "auto", "&:focus-visible": { border: "none" } }}
              />
            </Box>
          )}
        </ModalRoot>
      </Fade>
    </Modal>
  );
}
